import * as React from "react";
import { useOfferModeWithoutFallback } from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import {
  USER_FIRST_NAME_PLACEHOLDER,
  USER_LAST_NAME_PLACEHOLDER,
} from "@nju/data-access-portal-user";
import { useClient } from "@nju/data-access-sales-client";
import { countries } from "@nju/static-data";
import { Cards, Modal, useOverlay } from "@nju/ui";
import { capitalizeFirstLetterOfEachWord } from "@nju/utils";
import { AddAddressModal } from "./addAddress";
import { getDocumentName, setOrderId } from "./utils";

interface IClientOrderInfoBoxProps {
  onUpdateAddress?: () => void;
  showAddress?: boolean;
  address?: {
    municipality?: string;
    street?: string;
    streetNumber?: string;
    apartmentNumber?: string;
  };
}

export function ClientOrderInfoBox({
  onUpdateAddress,
  showAddress,
  address,
}: IClientOrderInfoBoxProps) {
  const { ftth, multiOrder } = useOrchestrators();
  const data = useClient();
  const addressModal = useOverlay();
  const mode = useOfferModeWithoutFallback();
  const orderId = setOrderId({ mode, ftth, multiOrder });

  const client =
    data?.firstName !== USER_FIRST_NAME_PLACEHOLDER &&
    data?.lastName !== USER_LAST_NAME_PLACEHOLDER
      ? capitalizeFirstLetterOfEachWord(`${data?.firstName} ${data?.lastName}`)
      : "w trakcie uzupełniania";
  const email = data?.email || "...";
  const isVerifiedLabel = data?.isPersonVerified
    ? "zweryfikowany"
    : "niezweryfikowany";

  const residentialAddress = data?.person?.addresses?.find((item) => {
    return item?.type === "RESIDENTIAL";
  })?.geographicalAddress;

  const registration = data.person?.registrations?.[0];
  const document = registration?.typeCode;
  const documentType = document && getDocumentName(document);
  const pesel = document === "PESEL" && registration?.id;
  const nationality = registration?.issuingCountry;
  const numberDocument = registration?.id;
  const issuingCountry = countries.find((item) => {
    return item.id === nationality;
  })?.value;

  const columns = React.useMemo(() => {
    return [
      {
        key: "client",
        label: "imię i nazwisko:",
        value: client,
      },
      {
        key: "verification",
        label: "weryfikacja tożsamości:",
        value: isVerifiedLabel,
      },
      {
        key: "email",
        label: "e-mail:",
        value: email,
      },
      {
        key: "orderId",
        label: "nr zamówienia:",
        value: orderId || "-",
        copyable: !!orderId,
      },
    ];
  }, [client, isVerifiedLabel, email, orderId]);

  const columnsDetails = React.useMemo(() => {
    return [
      {
        key: "documentType",
        label: "rodzaj dokumentu:",
        value: (documentType !== "pesel" && documentType) || "-",
      },
      {
        key: "numberDocument",
        label: "numer dokumentu:",
        value: (documentType !== "pesel" && numberDocument) || "-",
      },
      {
        key: "pesel",
        label: "PESEL",
        value: pesel || "-",
      },
      {
        key: "issuingCountry",
        label: "obywatelstwo:",
        value: issuingCountry || "-",
      },
    ];
  }, [documentType, numberDocument, pesel, issuingCountry]);

  return (
    <div
      className="border-b-1 border-b-geyser"
      data-cy="user-information-panel"
    >
      <Cards
        addressModal={addressModal}
        columns={columns}
        columnsDetails={columnsDetails}
        residentialAddress={address ?? residentialAddress}
        showAddress={showAddress}
      />
      {addressModal.isOpened && (
        <Modal onClose={addressModal.close}>
          <AddAddressModal
            onClose={addressModal.close}
            onConfirm={onUpdateAddress}
          />
        </Modal>
      )}
    </div>
  );
}
